import {
  Box,
  Card,
  Flex,
  Label,
  Link,
  Text,
  Tooltip,
  useAppTheme,
} from '@workwhile/ui'
import { formatCurrency } from 'lib/currency'
import { getDurationInHours, getLocalTimezone } from 'lib/time'
import { AdminCharge, AdminChargeShift } from 'api/admin/charges/getCharges'
import { formatDuration } from 'date-fns'
import { formatInTimeZone } from 'date-fns-tz'
import { CalendarClock, ExternalLink, FileWarning } from 'lucide-react'
import React from 'react'
import {
  formatChargesDate,
  formatChargesDateTime,
  formatChargesRelative,
} from '../formatDateTime'
import { useChargesContextValue } from '../context/ChargesProvider'
import { ChargeItemRow } from './ChargeItemRow'
import { Detail, DetailRow } from './Detail'

function toNumber(value: string | number): number {
  return typeof value === 'number' ? value : Number(value)
}

function formatShiftDay(shift: AdminChargeShift): string {
  const timezone = shift.timezone ?? getLocalTimezone()
  return formatInTimeZone(shift.startsAt, timezone, 'EEE, MMM d, yyyy')
}

function formatShiftTimes(shift: AdminChargeShift): string {
  const timezone = shift.timezone ?? getLocalTimezone()
  const startTime = formatInTimeZone(shift.startsAt, timezone, 'h:mm a')
  const endTime = formatInTimeZone(shift.endsAt, timezone, 'h:mm a z')
  return `${startTime} – ${endTime}`
}

function formatShiftDuration(shift: AdminChargeShift): string {
  return formatDuration({
    hours: getDurationInHours(shift.startsAt, shift.endsAt),
  })
}

type ChargeCardProps = {
  charge: AdminCharge
}

export const ChargeCard = ({ charge }: ChargeCardProps) => {
  const { colors } = useAppTheme()
  const {
    state: { companyId },
  } = useChargesContextValue()
  const shift = charge.shift
  const total = toNumber(charge.totalAmount)

  return (
    <Card
      shadowless
      borderColor={colors.neutrals[100]}
      mb={2}
      width="100%"
      style={{ alignSelf: 'flex-start' }}
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        gap={3}
        flexWrap="wrap"
        mb={2}
      >
        <Flex alignItems="center" gap={2} flexWrap="wrap">
          <Text fontSize={2} fontWeight={700}>
            Charge #{charge.id}
          </Text>
          {charge.finalized ? (
            <Tooltip content="Charge is locked and can be included on an invoice">
              <Label variant="success" size="small">
                Finalized
              </Label>
            </Tooltip>
          ) : (
            <Tooltip content="Charge is still open and can continue to change">
              <Label variant="warning" size="small">
                Open
              </Label>
            </Tooltip>
          )}
        </Flex>
        <Text
          fontSize={2}
          fontWeight={700}
          color={total < 0 ? 'error' : 'text'}
          style={{ whiteSpace: 'nowrap' }}
        >
          {formatCurrency(total)}
        </Text>
      </Flex>

      {shift != null ? (
        <Box
          px={3}
          py={2}
          mb={2}
          backgroundColor={colors.neutrals[50]}
          style={{
            borderLeft: `3px solid ${colors.neutrals[200]}`,
            borderRadius: 4,
          }}
        >
          <DetailRow>
            <Detail label="Shift">
              <Flex alignItems="center" gap={1}>
                <CalendarClock size={13} color={colors.lightText} />
                <Link to={`/shifts/${shift.id}`} target="_blank">
                  #{shift.id}
                </Link>
                <ExternalLink size={11} color={colors.lightText} />
              </Flex>
            </Detail>
            <Detail label="Position">
              {shift.position.name}{' '}
              <Link
                to={`/position_editor/?positionId=${shift.position.id}`}
                target="_blank"
              >
                #{shift.position.id}
              </Link>
            </Detail>
            <Detail label="Location">
              {shift.location.name}{' '}
              <Text as="span" color="lightText">
                #{shift.location.id}
              </Text>
            </Detail>
            <Detail
              label="Shift day"
              title={formatChargesDateTime(shift.startsAt)}
            >
              {formatShiftDay(shift)}{' '}
              <Text as="span" color="lightText">
                ({formatChargesRelative(shift.startsAt)})
              </Text>
            </Detail>
            <Detail label="Scheduled">{formatShiftTimes(shift)}</Detail>
            <Detail label="Length">{formatShiftDuration(shift)}</Detail>
          </DetailRow>
        </Box>
      ) : (
        <Flex alignItems="center" gap={2} mb={2}>
          <FileWarning size={14} color={colors.lightText} />
          <Text fontSize={1} color="lightText">
            No shift — standalone charge (manual or adjustment)
          </Text>
        </Flex>
      )}

      <Box>
        <Text
          fontSize={0}
          color="lightText"
          mb={1}
          style={{ textTransform: 'uppercase', letterSpacing: '0.06em' }}
        >
          Line items ({charge.items.length})
        </Text>
        {charge.items.length === 0 ? (
          <Text fontSize={1} color="lightText">
            No charge items.
          </Text>
        ) : (
          charge.items.map((item) => (
            <ChargeItemRow
              key={item.id}
              item={item}
              shiftId={shift?.id ?? null}
              timezone={shift?.timezone}
            />
          ))
        )}
      </Box>

      <Flex gap={3} flexWrap="wrap" alignItems="center" mt={2}>
        <Text
          fontSize={0}
          color="lightText"
          title={formatChargesDateTime(charge.createdAt)}
        >
          Charge created {formatChargesDate(charge.createdAt)} (
          {formatChargesRelative(charge.createdAt)})
        </Text>
        {companyId == null ? (
          <Text fontSize={0} color="lightText">
            Company{' '}
            <Link to={`/companies/${charge.companyId}`} target="_blank">
              #{charge.companyId}
            </Link>
          </Text>
        ) : null}
      </Flex>
    </Card>
  )
}
